import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, MaskedLine, FadeIn, EASE } from "../components/motion";
import { CasesSection } from "../components/CasesSection";
import { FieldsCarousel } from "../components/FieldsCarousel";
import { FokusSection } from "../components/FokusSection";
import { FormatsSection } from "../components/FormatsSection";
import { ContactSection } from "../components/ContactSection";
import { trackEvent } from "../analytics/ga4";

const Hero = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const h = t.hero;
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -80]);

  const scrollTo = (id) => {
    if (id === "kontakt") {
      navigate("/#kontakt");
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-deep">
      <motion.div style={{ y: videoY }} className="absolute inset-0 will-change-transform">
        <video
          data-testid="hero-video"
          className="h-full w-full scale-105 object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/images/hero-poster.jpg"
        >
          <source src="/videos/hero-waves.mp4" type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-deep/40" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-deep/70 to-transparent" />

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-center px-6 pb-16 pt-24 sm:justify-end sm:px-12 sm:pb-28 sm:pt-0 md:px-16"
      >
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="text-[10px] font-medium uppercase tracking-label text-white/60 sm:text-[11px]"
        >
          {h.eyebrow}
        </motion.p>

        <h1 className="font-head mt-6 text-[13vw] leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-8xl">
          <span className="block overflow-hidden">
            <motion.span
              className="block will-change-transform"
              initial={reduce ? false : { y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
            >
              {h.line1}
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              className="block will-change-transform"
              initial={reduce ? false : { y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.3, delay: 0.7, ease: EASE }}
            >
              {h.line2}
            </motion.span>
          </span>
        </h1>

        <div className="shrink-0 overflow-hidden">
          <motion.p
            data-testid="hero-accent-line"
            className="font-head mt-5 text-xl text-wave sm:text-3xl"
            style={{ textShadow: "0 1px 3px rgba(0, 30, 40, 0.65)" }}
            initial={reduce ? false : { y: "120%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
          >
            {h.accent}
          </motion.p>
        </div>

        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.15 }}
          className="mt-7 max-w-xl text-sm font-light leading-relaxed text-white/70 sm:text-base"
        >
          {h.sub}
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.35, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => {
              trackEvent("contact_cta_click", { placement: "hero" });
              scrollTo("kontakt");
            }}
            className="group inline-flex items-center gap-4 bg-white px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-all duration-500 hover:bg-wave hover:text-white"
          >
            {h.ctaPrimary}
            <span className="h-px w-6 bg-current transition-all duration-500 group-hover:w-10" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("philosophie")}
            className="inline-flex items-center gap-4 border border-white/30 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition-all duration-500 hover:border-white hover:bg-white/10"
          >
            {h.ctaSecondary}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1.2 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 sm:right-12 md:right-16 lg:flex"
      >
        <span className="text-[10px] uppercase tracking-label text-white/50">{h.scroll}</span>
        <span className="relative h-14 w-px overflow-hidden bg-white/20">
          <motion.span
            className="absolute left-0 top-0 h-6 w-px bg-wave"
            animate={reduce ? {} : { y: [-24, 56] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
};

const HumanStatement = () => {
  const { t } = useLanguage();
  const h = t.human;
  return (
    <section id="philosophie" data-testid="human-statement-section" className="overflow-hidden bg-white">
      <div className="mx-auto max-w-[1600px] px-6 pb-12 pt-48 sm:px-12 md:px-16">
        <div className="grid items-start gap-12 md:grid-cols-12 md:gap-16">
          <div className="relative md:col-span-5 md:pt-0">
            <Reveal y={14}>
              <p className="mb-5 text-[13px] font-medium uppercase tracking-label text-wave">
                {h.label}
              </p>
            </Reveal>
            <h2 className="font-head relative max-w-xl text-5xl leading-[1.12] text-ink md:text-[2.25rem] lg:text-[3rem] xl:text-6xl">
              <MaskedLine delay={0.1}>{h.line1}</MaskedLine>
              <MaskedLine delay={0.22}>
                <span className="text-deep">{h.line2}</span>
              </MaskedLine>
            </h2>
            <div className="mt-10">
              <Reveal delay={0.3} className="max-w-md">
                <p className="text-base font-light leading-relaxed text-ink/70 sm:text-lg">{h.body}</p>
              </Reveal>
              <Reveal delay={0.45} className="mt-16 lg:mt-40">
                <p className="font-head max-w-sm border-l-2 border-wave pl-5 text-xl leading-snug text-deep sm:text-2xl">
                  {h.question}
                </p>
              </Reveal>
            </div>
          </div>
          <FadeIn delay={0.2} className="md:col-span-6 md:col-start-7">
            <div className="overflow-hidden">
              <img
                src="/images/david-umdasch.jpg"
                alt={h.imgAlt}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover object-top md:aspect-auto md:h-[calc(100svh-96px)]"
              />
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

const StatementSection = () => {
  const { t } = useLanguage();
  const s = t.statement;
  return (
    <section data-testid="positioning-statement" className="bg-[#F3F7F9]">
      <div className="mx-auto max-w-[1600px] px-6 py-32 sm:px-12 sm:py-48 md:px-16">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal y={10}>
            <span className="mx-auto block h-12 w-px bg-wave" aria-hidden="true" />
          </Reveal>
          <blockquote className="font-head mt-12 text-4xl leading-[1.15] text-ink sm:text-6xl">
            <MaskedLine delay={0.1}>{s.line1}</MaskedLine>
            <MaskedLine delay={0.24}>
              <span className="text-deep">{s.line2}</span>
            </MaskedLine>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

const AboutTeaser = () => {
  const { t } = useLanguage();
  const a = t.aboutTeaser;
  return (
    <section data-testid="about-teaser-section" className="bg-white">
      <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
        <div className="grid items-center gap-14 md:grid-cols-12">
          <FadeIn className="md:col-span-5">
            <div className="relative">
              <div className="overflow-hidden">
                <img
                  src="/images/77-DSC05240.jpg"
                  alt="Porträt von David Frank"
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 -right-3 h-24 w-24 border border-wave sm:-right-5" aria-hidden="true" />
            </div>
          </FadeIn>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal y={14}>
              <p className="text-[13px] font-medium uppercase tracking-label text-wave">{a.label}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <h2 className="font-head mt-8 text-3xl leading-tight text-ink sm:text-5xl">{a.title}</h2>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-8 text-lg font-light leading-relaxed text-ink/80 sm:text-xl">{a.text}</p>
              <Link
                to="/ueber-mich"
                data-testid="about-teaser-link"
                className="group mt-10 inline-flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-colors hover:text-deep"
              >
                {a.cta}
                <span className="h-px w-8 bg-current transition-all duration-500 group-hover:w-12" />
              </Link>
              <a
                data-testid="about-teaser-linkedin-link"
                href="https://www.linkedin.com/in/david-frank-780b501b1/"
                target="_blank"
                rel="noopener noreferrer"
                className="group ml-10 mt-10 inline-flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.22em] text-deep transition-colors hover:text-ink"
              >
                {a.linkedin}
                <span aria-hidden="true">↗</span>
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <HumanStatement />
      <CasesSection />
      <FieldsCarousel />
      <StatementSection />
      <FokusSection />
      <FormatsSection />
      <AboutTeaser />
      <ContactSection />
    </main>
  );
}
